import {IDL} from '@icp-sdk/core/candid';
import type {SatelliteParameters} from '@junobuild/ic-client/actor';
import {toPrincipal} from '@junobuild/ic-client/utils';
import {isNullish} from '@junobuild/utils';
import {
  listControllers,
  listDeprecatedControllers,
  listDeprecatedNoScopeControllers
} from '../api/satellite.api';
import {INSTALL_MODE_RESET, INSTALL_MODE_UPGRADE} from '../constants/upgrade.constants';
import {upgrade} from '../handlers/upgrade.handlers';
import type {UpgradeCodeParams} from '../types/upgrade';
import {encodeAdminAccessKeysToIDL} from '../utils/idl.utils';

/**
 * Upgrades a satellite with the provided WASM module.
 * @param {Object} params - The parameters for upgrading the satellite.
 * @param {SatelliteParameters} params.satellite - The satellite parameters, including the actor and satellite ID.
 * @param {Uint8Array} params.wasmModule - The WASM module to be installed during the upgrade.
 * @param {boolean} params.deprecated - Indicates whether the upgrade is deprecated.
 * @param {boolean} params.deprecatedNoScope - Indicates whether the upgrade is deprecated and has no scope.
 * @param {boolean} [params.reset=false] - Optional. Specifies whether to reset the satellite (reinstall) instead of performing an upgrade. Defaults to `false`.
 * @param {boolean} [params.preClearChunks] - Optional. Forces clearing existing chunks before uploading a chunked WASM module. Recommended if the WASM exceeds 2MB.
 * @param {boolean} [params.takeSnapshot=true] - Optional. Whether to take a snapshot before performing the upgrade. Defaults to true.
 * @param {function} [params.onProgress] - Optional. Callback function to track progress during the upgrade process.
 * @throws {Error} Will throw an error if the satellite parameters are invalid or missing required fields.
 * @returns {Promise<void>} Resolves when the upgrade process is complete.
 */
export const upgradeSatellite = async ({
  satellite,
  deprecated,
  deprecatedNoScope,
  reset = false,
  ...rest
}: {
  satellite: SatelliteParameters;
  deprecated: boolean;
  deprecatedNoScope: boolean;
  reset?: boolean;
} & Pick<
  UpgradeCodeParams,
  'wasmModule' | 'preClearChunks' | 'takeSnapshot' | 'onProgress'
>): Promise<void> => {
  const {satelliteId, ...actor} = satellite;

  if (isNullish(satelliteId)) {
    throw new Error('No satellite principal defined.');
  }

  // TODO: remove agent-js "type mismatch: type on the wire principal"
  if (deprecated) {
    const controllers = await listDeprecatedControllers({satellite});

    const arg = IDL.encode(
      [
        IDL.Record({
          controllers: IDL.Vec(IDL.Principal)
        })
      ],
      [{controllers}]
    );

    await upgrade({
      actor,
      canisterId: toPrincipal(satelliteId),
      arg: new Uint8Array(arg),
      mode: reset ? INSTALL_MODE_RESET : INSTALL_MODE_UPGRADE,
      reset,
      ...rest
    });

    return;
  }

  const list = deprecatedNoScope ? listDeprecatedNoScopeControllers : listControllers;

  // We pass the controllers to the upgrade but, it's just for the state of the art when upgrading because I don't want to call the install without passing args. The module's post_upgrade do not consider the init parameters.
  // On the contrary those are useful on --reset
  const controllers = await list({satellite, certified: reset});

  const arg = encodeAdminAccessKeysToIDL(controllers);

  await upgrade({
    actor,
    canisterId: toPrincipal(satelliteId),
    arg,
    mode: reset ? INSTALL_MODE_RESET : INSTALL_MODE_UPGRADE,
    reset,
    ...rest
  });
};
